// Imágenes de una prenda: miniaturas, agregar (hasta 3), quitar y ver en grande.
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { SupabaseClient } from '@supabase/supabase-js';
import { MAX_IMAGENES, enlaceDescarga, enlacesDe, subirImagen, vistaDe } from '../datos/imagenes';

// Enlaces de la vista (para mostrar) y del original (para descargar) de cada imagen.
function useEnlaces(sb: SupabaseClient, rutas: string[]) {
  const [enlaces, setEnlaces] = useState<Map<string, string>>(new Map());
  useEffect(() => { if (rutas.length) enlacesDe(sb, rutas.flatMap(r => [vistaDe(r), r])).then(setEnlaces).catch(() => setEnlaces(new Map())); }, [rutas.join('|')]);
  return enlaces;
}
// Las imágenes subidas antes de guardar originales no tienen vista: se muestran con su propio archivo.
const verDe = (enlaces: Map<string, string>, r: string) => enlaces.get(vistaDe(r)) || enlaces.get(r) || '';

// Visor a pantalla completa: se cierra con un toque, con "Cerrar" o con Escape.
// La escucha de Escape se activa al abrir (no después de pintar) y el foco pasa a "Cerrar", para quien usa el teclado.
export function Visor({ url, original, onCerrar }: { url: string; original?: string; onCerrar: () => void }) {
  const cerrar = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    const antes = document.activeElement as HTMLElement | null;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onCerrar(); } };
    addEventListener('keydown', k); cerrar.current?.focus();
    return () => { removeEventListener('keydown', k); antes?.focus?.(); };
  }, []);
  return (
    <div class="visor" role="dialog" aria-modal="true" aria-label="Imagen ampliada" onClick={onCerrar}>
      <img src={url} alt="Imagen ampliada" />
      <div class="fila-botones">
        {original && <a class="btn" href={original} onClick={e => e.stopPropagation()}>Descargar original</a>}
        <button ref={cerrar} class="btn" type="button" onClick={onCerrar}>Cerrar</button>
      </div>
    </div>
  );
}

// nombre: base del archivo que se descarga ("PED-0001-pantaloneta-nino-1.png"); sin él queda "talentino-imagen-1".
export function Miniaturas({ sb, rutas, nombre, onQuitar }: { sb: SupabaseClient; rutas: string[]; nombre?: string; onQuitar?: (ruta: string) => void }) {
  const enlaces = useEnlaces(sb, rutas), [ver, setVer] = useState<number | null>(null);
  if (!rutas.length) return null;
  const r = ver === null ? '' : rutas[ver] || '', ext = r.match(/\.([a-z0-9]+)$/i)?.[1] || 'jpg';
  return (
    <div class="miniaturas">
      {rutas.map((x, k) => (
        <div class="miniatura" key={x}>
          <button type="button" class="mini-ver" aria-label={'Ver imagen ' + (k + 1)} onClick={() => verDe(enlaces, x) && setVer(k)}>
            {verDe(enlaces, x) ? <img src={verDe(enlaces, x)} alt={'Imagen ' + (k + 1)} loading="lazy" /> : <span class="muted">…</span>}
          </button>
          {onQuitar && <button type="button" class="mini-quitar" aria-label={'Quitar imagen ' + (k + 1)} onClick={() => onQuitar(x)}>✕</button>}
        </div>
      ))}
      {r && <Visor url={verDe(enlaces, r)} original={enlaces.get(r) ? enlaceDescarga(enlaces.get(r)!, `${nombre || 'talentino-imagen'}-${ver! + 1}.${ext}`) : undefined} onCerrar={() => setVer(null)} />}
    </div>
  );
}

export function Imagenes({ sb, id, rutas, nombre, onAgregar, onQuitar }: { sb: SupabaseClient; id: string; rutas: string[]; nombre?: string; onAgregar: (nuevas: string[]) => void; onQuitar: (ruta: string) => void }) {
  const [subiendo, setSubiendo] = useState(0), [error, setError] = useState('');
  const agregar = async (archivos: File[]) => {
    setError('');
    const lista = archivos.slice(0, Math.max(0, MAX_IMAGENES - rutas.length));
    if (archivos.length > lista.length) setError(`Cada prenda lleva hasta ${MAX_IMAGENES} imágenes.`);
    if (!lista.length) return;
    setSubiendo(lista.length);
    const nuevas: string[] = [];
    for (const f of lista) { try { nuevas.push(await subirImagen(sb, f)); } catch (e) { setError((e as Error).message); } }
    setSubiendo(0);
    if (nuevas.length) onAgregar(nuevas);
  };
  return (
    <div class="field imagenes">
      <span class="lbl">Imágenes <span class="muted">(dibujo, huellitas o referencia; hasta {MAX_IMAGENES}, en tamaño original)</span></span>
      <div class="imagenes-fila">
        <Miniaturas sb={sb} rutas={rutas} nombre={nombre} onQuitar={r => { setError(''); onQuitar(r); }} />
        {subiendo > 0 && <span class="hint">Subiendo {subiendo === 1 ? 'la imagen' : subiendo + ' imágenes'}…</span>}
        {rutas.length < MAX_IMAGENES && !subiendo && <>
          <label class="btn sm" for={id}>+ Agregar imagen</label>
          <input id={id} class="oculto" type="file" accept="image/*" multiple onChange={e => { const fs = [...(e.currentTarget.files || [])]; e.currentTarget.value = ''; agregar(fs); }} />
        </>}
      </div>
      {error && <span class="errtxt">{error}</span>}
    </div>
  );
}
